import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { SelectOption } from '@/shared/types';
import { ConditionView, FormSelect, FormSwitch, Grid, StepControlPanel, Loader } from '@/shared/ui';

import {
  ExtractFormType,
  ExtractTypeSelectOptions,
} from '@/modules/iac/pages/services/ExtendedCriminalRecord/lib/constants';
import { ExtendedCriminalRecordExtractSchema } from '@/modules/iac/pages/services/ExtendedCriminalRecord/model/types';

import { FormDataType } from '@/api/types';

import api from '@/api';

export type PurposeAndExtractTypeStepProps = {
  handleApostilleRequiredChange: (value: boolean) => void;
};

export const PurposeAndExtractTypeStep: React.FC<StepProps<PurposeAndExtractTypeStepProps>> = ({
  next,
  prev,
  handleApostilleRequiredChange,
}) => {
  const { control } = useFormContext<ExtendedCriminalRecordExtractSchema>();
  const [requestPurposeOptions, setRequestPurposeOptions] = useState<SelectOption[]>([]);
  const extractFormType = useWatch({
    control,
    name: 'extractFormType',
  });
  const hasApostille = useWatch({
    control,
    name: 'hasApostille',
  });

  useEffect(() => {
    api.dictionary
      .getProposal()
      .then(({ data }) => setRequestPurposeOptions(data.map(({ id, name }) => ({ id, label: name }))));
  }, []);

  useEffect(() => {
    handleApostilleRequiredChange(!!hasApostille);
  }, [hasApostille]);

  return (
    <>
      <ConditionView if={!isEmpty(requestPurposeOptions)} else={<Loader centered />}>
        <FormBlock title="Мета запиту і тип витягу">
          <FormSelect
            name="requestPurpose"
            label="Мета запиту"
            control={control}
            options={requestPurposeOptions}
            required
          />
          <FormSelect
            name="extractType"
            label="Тип витягу"
            control={control}
            options={Object.values(ExtractTypeSelectOptions)}
            required
          />
          <ConditionView if={extractFormType === ExtractFormType.PAPER}>
            <FormSwitch name="hasApostille" control={control} label="Необхідний апостиль" />
          </ConditionView>
        </FormBlock>
        <StepControlPanel next={next} prev={prev} />
      </ConditionView>
    </>
  );
};

export const PurposeAndExtractTypePreview: React.FC = () => {
  const { getValues } = useFormContext<FormDataType>();
  const formData = getValues();
  const extractFormType = formData.extractFormType as Nullable<ExtractFormType>;
  const requestPurpose = formData.requestPurpose as Nullable<SelectOption>;
  const extractType = formData.extractType as Nullable<SelectOption>;
  const hasApostille = formData.hasApostille as Nullable<boolean>;

  const data: Array<[string, Nullable<string>]> = [
    ['Мета запиту', requestPurpose?.label ?? null],
    ['Тип витягу', extractType?.label ?? null],
  ];

  if (extractFormType === ExtractFormType.PAPER) {
    data.push(['Необхідний апостиль', hasApostille ? 'Так' : 'Ні']);
  }

  return <Grid title="Мета запиту і тип витягу" data={data} />;
};
